import { useQueryClient } from "@tanstack/react-query";
import { useMachine } from "../queries/machine/use-machine";
import { signal } from "./message";
import { useSignal } from "./use-signal";
import { taskKeys } from "../queries/tasks/task-keys";
import { useWorkOrderOperation } from "../queries/work-order/use-work-order-operation";
import { useWorkOrder } from "../queries/work-order/use-work-order";
import { ODataCollectionResponseV4 } from "@odata2ts/odata-core";
import { Task } from "../queries/tasks/use-tasks";
import { workOrderKeys } from "@/queries/work-order/work-order-keys";
import { mtuKeys } from "@/queries/mtu/mtu-keys";
import { useWorkOrderOperationId } from "@/queries/work-order/use-work-order-operation-id";
import { useMTUs } from "@/queries/mtu/use-mtus";

interface TaskStatusChangedMessage {
  EnvelopeCategory: string;
  EnvelopeTopic: string;
  EnvelopeModule: string;
  EnvelopeTag: string;
  EnvelopeUserField1: string;
  EnvelopeUserField2: null;
  EnvelopeUserField3: string;
  EnvelopeUserField4: string;
  EnvelopeUserField5: string;
  EnvelopeUserField6: string;
  EnvelopeUserField7: null;
  EnvelopeUserField8: null;
  EnvelopeUserField9: string;
  EnvelopeUserField10: string;
  StatusInfo: {
    StateMachineNId: string;
    PreviousStatusNId: null;
    CurrentStatusNId: string;
    EntityId: string;
    EntityType: string;
    StatusOutcome: number;
    CurrentStatusColor: string;
    CurrentStatusName: string;
    LastUpdatedOn: string;
    CreatedOn: string;
  };
  TaskInfo: {
    TaskId: string;
    TaskNId: string;
    TaskTypeNId: string;
    TaskDefinitionNId: string;
    TaskDefinitionRevision: string;
    ErrorCount: number;
    IsSkippable: boolean;
    EquipmentNId: string;
    Sequence: number;
    MaxIterations: number;
    IterationGroupId: string;
    ContinueOnSuccess: boolean;
    ContinueOnFailure: boolean;
    TaskFlowIdentifier: string;
  };
}

function taskStatusChangedSignal(machineId?: string | null) {
  return signal<{ d: [TaskStatusChangedMessage] }>({
    app: "Task",
    channel: "TaskStatusChanged",
    filters: `EnvelopeModule eq 'TaskManagement' and EnvelopeTopic eq 'Task' and TaskInfo/EquipmentNId eq '${machineId}'`,
    enabled: !!machineId,
  });
}

export function useTaskStatusChanged() {
  const { machine } = useMachine();
  const { workOrderOperation } = useWorkOrderOperation();
  const { workOrderOperationId } = useWorkOrderOperationId();
  const { workOrder } = useWorkOrder();
  const machineId = machine?.EquipmentNId;
  const queryClient = useQueryClient();
  const { selected, setSelected, MTUs } = useMTUs();

  const taskKey = taskKeys.listAll(
    machineId,
    workOrderOperation?.NId,
    workOrder?.NId,
    workOrder?.FinalMaterial.Material.UId
  );

  function switchToNextMtu(tasks: Task[], updatedTaskId: string) {
    const updatedTask = tasks.find((x) => x.Id === updatedTaskId);
    const taskMtu = updatedTask?.Description;
    const isSelectedMtu =
      selected?.DM_MaterialTrackingUnit.MaterialTrackingUnit.Code === taskMtu;

    const hasPendingTask = tasks.some(
      (x) => x.Description === taskMtu && x.Status?.StatusNId !== "Completed"
    );

    const nextPendingTask = tasks.find(
      (x) => x.Status?.StatusNId !== "Completed"
    );

    const mtu = MTUs?.find(
      (x) =>
        x.DM_MaterialTrackingUnit.MaterialTrackingUnit.Code ===
        nextPendingTask?.Description
    );

    if (!isSelectedMtu || hasPendingTask || !mtu) return;

    setSelected(mtu.Id);
  }

  return useSignal(taskStatusChangedSignal(machineId), ({ d }) => {
    const [message] = d;

    const data = queryClient.setQueryData<
      ODataCollectionResponseV4<Task> | undefined
    >(taskKey, (oldData) => {
      const tasks = oldData?.value ?? [];
      const updatedTask = tasks.find((x) => x.Id === message.TaskInfo.TaskId);

      if (!updatedTask) return;

      const updatedTaskWithNewStatus: Task = {
        ...updatedTask,
        Status: {
          ...updatedTask.Status!,
          StatusNId: message.StatusInfo.CurrentStatusNId,
        },
      };

      // Update data inplace to prevent shifting order
      const updatedData = tasks.map((x) =>
        x.Id === message.TaskInfo.TaskId ? updatedTaskWithNewStatus : x
      );

      return {
        ...oldData,
        value: updatedData,
      };
    });
    queryClient.invalidateQueries({
      queryKey: taskKey,
    });
    queryClient.invalidateQueries({
      queryKey: mtuKeys.get(
        workOrderOperationId?.WorkOrderOperation_Id,
        workOrderOperationId?.Id
      ),
    });
    queryClient.invalidateQueries({
      queryKey: workOrderKeys.getOperationId(machine?.EquipmentNId),
    });

    if (data) switchToNextMtu(data.value, message.TaskInfo.TaskId);
  });
}
